import React, { FC, useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { View, Text, StyleSheet, ListRenderItem, InteractionManager } from 'react-native';
import { FlashList } from '@shopify/flash-list';
import { font } from '@styles/fonts';
import { scale } from '@components/frame';
import { useFocusEffect } from '@react-navigation/native';
import {
  DIFF_ROW_DOM_ID_PREFIX,
  ESTIMATED_ROW_HEIGHT,
  MAX_CONTENT_WIDTH,
  MAX_LOC,
} from './constants/diffView.constants';
import {
  NCFileDiffViewProps,
  VersionMetadata,
  NCFileVersionData,
  VersionPickerAnchorPosition,
  ProcessedDiff,
  DiffListItem,
} from './types';
import { processDiffWithContext, truncateContent } from './helpers/utils';
import { detectDialect } from './helpers/detectDialect';
import { performJumpToChange } from './helpers/performJumpToChange';
import { buildPreparedListItems } from './helpers/buildPreparedListItems';
import LoadingState from './LoadingState';
import ErrorState from './ErrorState';
import { useNCFileContentFetching } from './hooks/useNCFileContentFetching';
import { useExpandSection } from './hooks/useExpandSection';

import { PADDING_HORIZONTAL_FOR_SHADOW, SCREEN_BORDER_RADIUS, TEST_IDS } from '@v2/pages/common/constants/base.constants';
import i18n from 'i18n-js';
import VersionSelectionModal from './VersionSelectionModal';
import CloseButton from '@components/closeButton';
import DiffRow from './DiffRow';
import CollapsedSection from './CollapsedSection';
import { useScrollTopButton } from '@components/common/NCFile/hooks/useScrollTopButton';
import VersionHeader from './VersionHeader';
import { getBoxShadowStyle } from '@styles/shadow';
import { isPhone, isWeb } from '@helpers';
import ScrollToTopButton from './ScrollToTopButton';
import { WithColor } from '@styles/colors';
import getPcsCount from '@components/ncFileVersions/helpers/getPcsCount';
import ChangeSummaryPanel from './ChangeSummaryPanel';
import { ChangeSummaryEntry } from './types';


interface NCFileDiffViewWithColorProps extends NCFileDiffViewProps, WithColor {}

const NCFileDiffView: FC<NCFileDiffViewWithColorProps> = ({
  C,
  oldVersionData,
  newVersionData,
  getNCFile,
  availableVersions,
  onReplaceVersion,
  onRequestPicker,
  onClose,
  onToggleFavoriteVersion,
  ncFileName,
}) => {
  const dialect = detectDialect(ncFileName);

  const { leftVersion, rightVersion } = useMemo(() => {
    if (oldVersionData.sequenceNumber <= newVersionData.sequenceNumber) {
      return { leftVersion: oldVersionData, rightVersion: newVersionData };
    }

    return { leftVersion: newVersionData, rightVersion: oldVersionData };
  }, [oldVersionData, newVersionData]);

  const buildVersionMetadata = (version: NCFileVersionData, badgeLabel?: string): VersionMetadata => ({
    versionId: version.ncFileVersionId,
    version: version.sequenceNumber.toString(),
    badgeLabel,
    pcsCount: getPcsCount(version),
    isFavorite: version.isFavorite,
    firstEvent: version.firstRanOn,
    lastEvent: version.lastRanOn,
    firstMachine: version.firstRanByMachineName,
    lastMachine: version.lastRanByMachineName,
    memo: version.memo,
    isActive: version.isActive,
  });

  const oldVersionMetadata: VersionMetadata = useMemo(
    () => buildVersionMetadata(leftVersion, i18n.t('Oldest')),
    [leftVersion]
  );

  const newVersionMetadata: VersionMetadata = useMemo(
    () => buildVersionMetadata(rightVersion, i18n.t('Latest')),
    [rightVersion]
  );

  const [retryToken, setRetryToken] = useState(0);

  // Fetch NC file content for both versions
  const { oldContent, newContent, loading, error } = useNCFileContentFetching({
    oldVersion: leftVersion,
    newVersion: rightVersion,
    getNCFile,
    retrySignal: retryToken,
  });

  const [processingDiff, setProcessingDiff] = useState(true);
  const [processedDiff, setProcessedDiff] = useState<ProcessedDiff>({ oldLines: [], newLines: [] });
  const [isTruncated, setIsTruncated] = useState(false);
  // Native highlight: tick increments per click so DiffRow's effect re-fires even
  // when the same row is clicked twice in a row. Web uses the DOM overlay path.
  const [highlightState, setHighlightState] = useState<{ key: string; tick: number } | null>(null);
  const { showScrollTop, handleScroll } = useScrollTopButton();
  const flatListRef = useRef<FlashList<DiffListItem>>(null);
  // Web jump: hold pending poll + active flash in refs so a new jump (or unmount)
  // can tear down the previous one — otherwise rapid clicks stack.
  const webScrollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const webHighlightCancelRef = useRef<(() => void) | null>(null);

  const cancelPendingWebJump = useCallback(() => {
    if (webScrollTimerRef.current !== null) {
      clearTimeout(webScrollTimerRef.current);
      webScrollTimerRef.current = null;
    }
    if (webHighlightCancelRef.current) {
      webHighlightCancelRef.current();
      webHighlightCancelRef.current = null;
    }
  }, []);

  useEffect(() => cancelPendingWebJump, [cancelPendingWebJump]);

  const backgroundColor = C('Background/Light/Detail');
  const availableVersionList = useMemo(() => availableVersions ?? [], [availableVersions]);
  const [versionPicker, setVersionPicker] = useState<{
    targetVersion: NCFileVersionData;
    metadata: VersionMetadata;
    column: 'left' | 'right';
    anchor: VersionPickerAnchorPosition;
  } | null>(null);

  const openVersionPicker = (target: NCFileVersionData, metadata: VersionMetadata, column: 'left' | 'right', anchor: VersionPickerAnchorPosition) => {
    if (!onReplaceVersion || !availableVersionList.length) {
      return;
    }
    setVersionPicker({ targetVersion: target, metadata, column, anchor });
  };

  useFocusEffect(
    useCallback(() => {
      if (versionPicker && onRequestPicker) {
        onRequestPicker(versionPicker.column === 'left' ? 'old' : 'new');
      }
    }, [versionPicker, onRequestPicker])
  );

  const handleVersionSelection = (version: NCFileVersionData) => {
    if (!versionPicker || !onReplaceVersion) {
      return;
    }
    onReplaceVersion(versionPicker.targetVersion.ncFileVersionId, version);
    setVersionPicker(null);
  };

  // Run processDiffWithContext after interactions so navigation animations stay smooth.
  useEffect(() => {
    if (!oldContent || !newContent) {
      setProcessedDiff({ oldLines: [], newLines: [] });
      setProcessingDiff(false);
      setIsTruncated(false);
      return;
    }

    setProcessingDiff(true);
    setIsTruncated(false);

    const handle = InteractionManager.runAfterInteractions(() => {
      let processedOldContent = oldContent;
      let processedNewContent = newContent;
      let truncated = false;

      const oldLinesCount = (oldContent.match(/\n/g) || []).length + 1;
      const newLinesCount = (newContent.match(/\n/g) || []).length + 1;

      if (oldLinesCount > MAX_LOC || newLinesCount > MAX_LOC) {
        truncated = true;

        if (oldLinesCount > MAX_LOC) {
          processedOldContent = truncateContent(oldContent, MAX_LOC);
        }
        if (newLinesCount > MAX_LOC) {
          processedNewContent = truncateContent(newContent, MAX_LOC);
        }
      }

      setIsTruncated(truncated);

      const diff = processDiffWithContext(processedOldContent, processedNewContent);
      setProcessedDiff(diff);
      setProcessingDiff(false);
    });

    return () => handle.cancel();
  }, [oldContent, newContent]);

  // Derived (not state) so handleExpandSection's setProcessedDiff flows into the
  // FlashList and re-runs classifyChanges in the change-summary panel.
  const preparedListItems = useMemo<DiffListItem[]>(() => buildPreparedListItems(processedDiff), [processedDiff]);

  const handleScrollToTop = () => {
    flatListRef.current?.scrollToOffset({ offset: 0, animated: true });
  };

  const handleJumpToChange = useCallback((entry: ChangeSummaryEntry) => {
    performJumpToChange(entry, {
      list: flatListRef.current,
      preparedListItems,
      cancelPendingWebJump,
      webScrollTimerRef,
      webHighlightCancelRef,
      setHighlightState,
    });
  }, [preparedListItems, cancelPendingWebJump]);

  const handleExpandSection = useExpandSection({ setProcessedDiff, oldContent, newContent });

  const headerNode = (
    <View style={styles.headerWrapper}>
      <View style={styles.headerTextBlock}>
        <Text style={[font['Large Title'], styles.headerTitleText, { color: C('Label/Light/Primary') }]}>{i18n.t('Version Comparison')}</Text>
      </View>
      {onClose && (
        <View style={{ position: 'absolute', right: scale(32), top: scale(20) }}>
          <CloseButton C={C} onPress={onClose} />
        </View>
      )}
    </View>
  );

  const shadowStyles = getBoxShadowStyle({ type: 'Field', C });
  const isTablet = !isPhone && !isWeb;
  const sectionGap = isTablet ? 16 : scale(16);

  const renderItem: ListRenderItem<DiffListItem> = useCallback(({ item }) => {
    const rowDomId = `${DIFF_ROW_DOM_ID_PREFIX}${item.key}`;
    if (item.type === 'collapsed') {
      return (
        <View nativeID={rowDomId} style={styles.rowContainer}>
           <View style={[styles.columnWrapper, { marginRight: sectionGap }]}>
             <CollapsedSection C={C} section={item.section} onExpand={handleExpandSection} />
           </View>
           <View style={styles.columnWrapper}>
             <View style={styles.collapsedSpacer} />
           </View>
        </View>
      );
    }

    return (
      <DiffRow
        C={C}
        nativeID={rowDomId}
        oldLine={item.oldLine}
        newLine={item.newLine}
        isGroupStart={item.isGroupStart}
        isGroupEnd={item.isGroupEnd}
        isFirstLineInDocument={item.index === 0}
        groupPositionOld={item.groupPositionOld}
        groupPositionNew={item.groupPositionNew}
        highlightTick={highlightState?.key === item.key ? highlightState.tick : 0}
      />
    );
  }, [C, handleExpandSection, sectionGap, highlightState]);

  // Show loading state
  if (loading) {
    return (
      <View testID={TEST_IDS.ncFileVersions.diffView} style={[styles.container, { backgroundColor }]}>
        {headerNode}
        <View style={styles.stateWrapper}>
          <LoadingState C={C} message={i18n.t('Loading file comparison...')} />
        </View>
      </View>
    );
  }

  // Show error state
  if (error || !oldContent || !newContent) {
    return (
      <View testID={TEST_IDS.ncFileVersions.diffView} style={[styles.container, { backgroundColor }]}>
        {headerNode}
        <View style={styles.stateWrapper}>
          <ErrorState
            C={C}
            error={error || i18n.t('Failed to load file content')}
            onClose={onClose}
            onRetry={() => setRetryToken((prev) => prev + 1)}
          />
        </View>
      </View>
    );
  }

  return (
    <View testID={TEST_IDS.ncFileVersions.diffView} style={[styles.container, { backgroundColor }]}>
      {headerNode}

      <View style={styles.contentWrapper}>
        {/* Change Summary Panel — shown muted while diff is still processing so layout doesn't jump */}
        <ChangeSummaryPanel
          C={C}
          preparedListItems={preparedListItems}
          onJumpToChange={handleJumpToChange}
          dialect={dialect}
          disabled={processingDiff}
        />

        {/* Fixed Headers */}
        <View style={styles.headersContainer}>
           <View style={[styles.columnWrapper, { marginRight: sectionGap }]}>
              <View
                style={[
                  styles.versionCard,
                  shadowStyles,
                  {
                    backgroundColor: C('Background/Light/Field'),
                    borderRadius: scale(SCREEN_BORDER_RADIUS),
                  },
                ]}
              >
                <VersionHeader
                  C={C}
                  version={oldVersionMetadata}
                  onPress={
                    onReplaceVersion && availableVersionList.length
                      ? (anchor) => openVersionPicker(leftVersion, oldVersionMetadata, 'left', anchor)
                      : undefined
                  }
                  disabled={!Boolean(onReplaceVersion && availableVersionList.length)}
                  isExpanded={versionPicker?.targetVersion.ncFileVersionId === leftVersion.ncFileVersionId}
                  onToggleFavorite={onToggleFavoriteVersion ? () => onToggleFavoriteVersion(leftVersion) : undefined}
                />
              </View>
           </View>
           <View style={styles.columnWrapper}>
              <View
                style={[
                  styles.versionCard,
                  shadowStyles,
                  {
                    backgroundColor: C('Background/Light/Field'),
                    borderRadius: scale(SCREEN_BORDER_RADIUS),
                  },
                ]}
              >
                <VersionHeader
                  C={C}
                  version={newVersionMetadata}
                  onPress={
                    onReplaceVersion && availableVersionList.length
                      ? (anchor) => openVersionPicker(rightVersion, newVersionMetadata, 'right', anchor)
                      : undefined
                  }
                  disabled={!Boolean(onReplaceVersion && availableVersionList.length)}
                  isExpanded={versionPicker?.targetVersion.ncFileVersionId === rightVersion.ncFileVersionId}
                  onToggleFavorite={onToggleFavoriteVersion ? () => onToggleFavoriteVersion(rightVersion) : undefined}
                />
              </View>
           </View>
        </View>

        {/* Virtualized List */}
        {processingDiff ? (
          <View style={styles.processingWrapper}>
            <LoadingState C={C} message={i18n.t('Preparing comparison...')} />
          </View>
        ) : (
          <FlashList
            ref={flatListRef}
            data={preparedListItems}
            renderItem={renderItem}
            keyExtractor={(item) => item.key}
            estimatedItemSize={ESTIMATED_ROW_HEIGHT}
            contentContainerStyle={styles.listContent}
            onScroll={handleScroll}
            scrollEventThrottle={16}
            ListFooterComponent={
              isTruncated ? (
                <View style={styles.footerContainer}>
                  <Text style={[styles.footerText, { color: C('Label/Light/Secondary') }]}>
                    {i18n.t('Comparison limited to first 10,000 lines due to file size.')}
                  </Text>
                </View>
              ) : null
            }
          />
        )}
      </View>

      <ScrollToTopButton C={C} visible={showScrollTop} onPress={handleScrollToTop} />

      {versionPicker && (
        <VersionSelectionModal
          C={C}
          visible={Boolean(versionPicker)}
          versions={availableVersionList}
          currentVersion={versionPicker.metadata}
          otherVersionId={
            versionPicker.targetVersion.ncFileVersionId === leftVersion.ncFileVersionId ? rightVersion.ncFileVersionId : leftVersion.ncFileVersionId
          }
          anchorPosition={versionPicker.anchor}
          column={versionPicker.column}
          onClose={() => setVersionPicker(null)}
          onSelect={handleVersionSelection}
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    minHeight: 0,
  },
  headerWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: scale(32),
    paddingTop: scale(24),
    paddingBottom: scale(12),
  },
  headerTextBlock: {
    gap: scale(2),
    alignItems: 'center',
  },
  headerTitleText: {
    fontSize: scale(20),
    lineHeight: scale(24),
    textAlign: 'center',
  },
  stateWrapper: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: scale(32),
  },
  contentWrapper: {
    flex: 1,
    minHeight: 0,
    width: '100%',
    maxWidth: MAX_CONTENT_WIDTH,
    alignSelf: 'center',
    paddingHorizontal: scale(24),
  },
  headersContainer: {
    flexDirection: 'row',
    width: '100%',
    paddingBottom: scale(16),
    zIndex: 1,
  },
  columnWrapper: {
    flex: 1,
  },
  rowContainer: {
    flexDirection: 'row',
    width: '100%',
  },
  versionCard: {
    overflow: 'hidden',
  },
  listContent: {
    paddingBottom: scale(48),
    paddingHorizontal: scale(PADDING_HORIZONTAL_FOR_SHADOW),
  },
  footerContainer: {
    paddingVertical: scale(24),
    alignItems: 'center',
    justifyContent: 'center',
  },
  footerText: {
    fontSize: scale(14),
    textAlign: 'center',
  },
  processingWrapper: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  collapsedSpacer: {
    height: scale(40),
  },
});

export default NCFileDiffView;
